import { settings } from "../state/app";

/** A full-screen canvas for one short effect, removed when the effect ends. */
function layer(into?: HTMLElement | null): { ctx: CanvasRenderingContext2D; W: number; H: number; done: () => void } | null {
  if (settings.get().reduceMotion || matchMedia("(prefers-reduced-motion: reduce)").matches) return null;
  const canvas = document.createElement("canvas");
  canvas.className = "confetti";
  canvas.setAttribute("aria-hidden", "true");
  const dpr = Math.min(2, window.devicePixelRatio || 1);
  const W = window.innerWidth;
  const H = window.innerHeight;
  canvas.width = W * dpr;
  canvas.height = H * dpr;
  (into ?? document.body).appendChild(canvas);
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    canvas.remove();
    return null;
  }
  ctx.scale(dpr, dpr);
  return { ctx, W, H, done: () => canvas.remove() };
}

/** A short, self-cleaning confetti burst on a full-screen canvas: over the page, or inside `into`. */
export function confetti(colors: string[], { count = 140, duration = 2600, into = null as HTMLElement | null } = {}): void {
  const l = layer(into);
  if (!l) return;
  const { ctx, W, H } = l;
  const parts = Array.from({ length: count }, (_, i) => {
    const fromLeft = i % 2 === 0;
    const angle = (fromLeft ? -60 : -120) + (Math.random() * 40 - 20);
    const speed = 9 + Math.random() * 9;
    return {
      x: fromLeft ? -10 : W + 10,
      y: H * (0.55 + Math.random() * 0.2),
      vx: Math.cos((angle * Math.PI) / 180) * speed,
      vy: Math.sin((angle * Math.PI) / 180) * speed,
      w: 6 + Math.random() * 6,
      h: 8 + Math.random() * 8,
      rot: Math.random() * Math.PI,
      vr: (Math.random() - 0.5) * 0.3,
      color: colors[i % colors.length],
      shape: i % 5 === 0 ? "circle" : "rect",
    };
  });
  const t0 = performance.now();
  const frame = (now: number) => {
    const t = now - t0;
    ctx.clearRect(0, 0, W, H);
    const fade = Math.max(0, 1 - Math.max(0, t - duration * 0.6) / (duration * 0.4));
    for (const p of parts) {
      p.vy += 0.32;
      p.vx *= 0.99;
      p.vy *= 0.99;
      p.x += p.vx;
      p.y += p.vy;
      p.rot += p.vr;
      ctx.save();
      ctx.globalAlpha = fade;
      ctx.translate(p.x, p.y);
      ctx.rotate(p.rot);
      ctx.fillStyle = p.color;
      if (p.shape === "circle") {
        ctx.beginPath();
        ctx.arc(0, 0, p.w / 2, 0, Math.PI * 2);
        ctx.fill();
      } else {
        ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h * Math.abs(Math.cos(p.rot * 2)));
      }
      ctx.restore();
    }
    if (t < duration) requestAnimationFrame(frame);
    else l.done();
  };
  requestAnimationFrame(frame);
}

function star(ctx: CanvasRenderingContext2D, r: number): void {
  ctx.beginPath();
  for (let i = 0; i < 10; i++) {
    const rad = i % 2 ? r * 0.45 : r;
    const a = -Math.PI / 2 + (i * Math.PI) / 5;
    ctx.lineTo(Math.cos(a) * rad, Math.sin(a) * rad);
  }
  ctx.closePath();
  ctx.fill();
}

function heart(ctx: CanvasRenderingContext2D, r: number): void {
  ctx.beginPath();
  ctx.moveTo(0, r * 0.9);
  ctx.bezierCurveTo(-r * 1.3, 0, -r * 0.7, -r * 1.05, 0, -r * 0.4);
  ctx.bezierCurveTo(r * 0.7, -r * 1.05, r * 1.3, 0, 0, r * 0.9);
  ctx.fill();
}

function dot(ctx: CanvasRenderingContext2D, r: number): void {
  ctx.beginPath();
  ctx.arc(0, 0, r * 0.6, 0, Math.PI * 2);
  ctx.fill();
}

/** Stars, hearts or a puff of dots fanning out from a point and fading: a love, a like, a pass. */
export function burst(
  x: number,
  y: number,
  colors: string[],
  { count = 22, reach = 140, duration = 900, shape = "star" as "star" | "heart" | "dot" } = {},
): void {
  const l = layer();
  if (!l) return;
  const { ctx, W, H } = l;
  const parts = Array.from({ length: count }, (_, i) => ({
    a: (i / count) * Math.PI * 2 + Math.random() * 0.5,
    v: reach * (0.5 + Math.random() * 0.5),
    size: 5 + Math.random() * 7,
    spin: (Math.random() - 0.5) * 4,
    color: colors[i % colors.length],
  }));
  const draw = shape === "heart" ? heart : shape === "dot" ? dot : star;
  const t0 = performance.now();
  const frame = (now: number) => {
    const p = Math.min(1, (now - t0) / duration);
    const out = 1 - Math.pow(1 - p, 3);
    ctx.clearRect(0, 0, W, H);
    for (const q of parts) {
      // out quickly, drifting up, then settling as they fade
      const px = x + Math.cos(q.a) * q.v * out;
      const py = y + Math.sin(q.a) * q.v * out - 36 * out + 30 * p * p;
      ctx.save();
      ctx.globalAlpha = 1 - p * p;
      ctx.translate(px, py);
      ctx.rotate(q.spin * p);
      ctx.fillStyle = q.color;
      draw(ctx, q.size * (1 - 0.35 * p));
      ctx.restore();
    }
    if (p < 1) requestAnimationFrame(frame);
    else l.done();
  };
  requestAnimationFrame(frame);
}
